import {
  type CascoCampaign,
  type CascoCriteria,
  type GeneratorKey,
  leadGenerationCampaignsPartialUpdateMutation,
  leadGenerationCampaignsRetrieveQueryKey
} from '@crm/api'
import {
  criteriaFieldsByGenerator,
  parseRunTime,
  RUN_TIME_FORMAT,
  toApiRunTime
} from '@crm/features/lead-generation/utils'
import { Text } from '@shared/components'
import { useInvalidatePredicateQueries } from '@shared/hooks'
import { useLocales } from '@shared/locales'
import { showApiErrors } from '@shared/utils'
import { useMutation } from '@tanstack/react-query'
import {
  Button,
  Card,
  Col,
  Divider,
  Flex,
  Form,
  Input,
  notification,
  Row,
  Switch,
  TimePicker
} from 'antd'
import { useEffect } from 'react'

import { GeneratorTag } from '../GeneratorTag/GeneratorTag'

import type { Dayjs } from 'dayjs'

type FormValues = {
  name: string
  is_active: boolean
  run_time?: Dayjs | null
  criteria?: CascoCriteria
}

type Props = {
  campaign?: CascoCampaign
  generatorKey: GeneratorKey
  isLoading?: boolean
}

/**
 * The whole configuration of a campaign — its own settings and the selection
 * criteria of its generator — as one form saved in one request.
 *
 * The criteria live under a `criteria` key in the form exactly as they do in the
 * payload, so the two groups never collide on a field name and submitting needs
 * no regrouping.
 */
export const CampaignSettingsCard = ({
  campaign,
  generatorKey,
  isLoading
}: Props) => {
  const { t } = useLocales()
  const [form] = Form.useForm<FormValues>()

  const { invalidateQueries } = useInvalidatePredicateQueries()

  const CriteriaFields = criteriaFieldsByGenerator[generatorKey]

  useEffect(() => {
    if (!campaign) return

    form.setFieldsValue({
      name: campaign.name,
      is_active: campaign.is_active,
      run_time: parseRunTime(campaign.run_time),

      ...(campaign.criteria && {
        criteria: {
          ...campaign.criteria,
          // The API types this array as nullable, but antd's setFieldsValue
          // only accepts undefined for an absent list value.
          excluded_casco_packages:
            campaign.criteria.excluded_casco_packages ?? undefined
        }
      })
    })
  }, [campaign, form])

  const { mutate, isPending } = useMutation({
    ...leadGenerationCampaignsPartialUpdateMutation(),
    onError: (e) => showApiErrors(e, form),
    onSuccess: () => {
      invalidateQueries([
        leadGenerationCampaignsRetrieveQueryKey({
          path: { generator_key: generatorKey }
        })
      ])

      notification.success({
        message: t('success.success'),
        description: t('success.success_data_save')
      })
    }
  })

  const onSubmit = ({ name, is_active, run_time, criteria }: FormValues) =>
    mutate({
      path: { generator_key: generatorKey },
      body: {
        name,
        is_active,
        run_time: toApiRunTime(run_time),
        // Generators without a criteria form have nothing to send, and an empty
        // object here would overwrite whatever the backend holds.
        ...(criteria && { criteria })
      }
    })

  return (
    <Card loading={isLoading}>
      <Form
        form={form}
        layout="vertical"
        onFinish={onSubmit}
        requiredMark={false}
      >
        {/* One heading for the whole form: the campaign's own settings and the
            generator's criteria flow through a single grid, so there is no
            contiguous run of criteria to label on its own. */}
        <Flex align="baseline" gap={8} wrap className="mb-16">
          <Text size={16} weight={500}>
            {t('lead_generation.selection_criteria')}
          </Text>

          <Text type="secondary">
            {t('lead_generation.generator_specific')}
          </Text>
        </Flex>

        {/* Four across on large screens, two on small, stacked on the narrowest.
            The criteria contribute their own columns to this row, so both groups
            fill the width instead of each keeping half of it. */}
        <Row gutter={[18, 0]}>
          <Col span={24} sm={12} xl={6}>
            <Form.Item
              name="name"
              label={t('lead_generation.name')}
              rules={[{ required: true }]}
            >
              <Input />
            </Form.Item>
          </Col>

          <Col span={24} sm={12} xl={6}>
            <Form.Item
              name="is_active"
              label={t('lead_generation.status')}
              valuePropName="checked"
            >
              <Switch
                checkedChildren={t('lead_generation.active')}
                unCheckedChildren={t('lead_generation.inactive')}
              />
            </Form.Item>
          </Col>

          <Col span={24} sm={12} xl={6}>
            <Form.Item
              name="run_time"
              label={t('lead_generation.daily_run_time')}
            >
              <TimePicker format={RUN_TIME_FORMAT} className="w-full" />
            </Form.Item>
          </Col>

          <Col span={24} sm={12} xl={6}>
            <Form.Item label={t('lead_generation.generator')}>
              <GeneratorTag generatorKey={generatorKey} />
            </Form.Item>
          </Col>

          {CriteriaFields && <CriteriaFields />}
        </Row>

        {!CriteriaFields && (
          <Text type="secondary">{t('lead_generation.no_criteria_form')}</Text>
        )}

        <Divider />

        <Flex justify="flex-end">
          <Button type="primary" htmlType="submit" loading={isPending}>
            {t('buttons.save')}
          </Button>
        </Flex>
      </Form>
    </Card>
  )
}
